import ServerServices from "@/components/ServerServices";
import LiveNetwork from "@/components/LiveNetwork";
import { useEffect, useState } from "react";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip, CartesianGrid } from "recharts";
import { Activity, AlertTriangle, HardDrive } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Loading, StatusBadge, fmtBytes } from "@/components/common";
import { cn } from "@/lib/utils";

export default function Monitoring() {
  const [points, setPoints] = useState([]);
  const [sites, setSites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/monitoring/points"),
      api.get("/monitoring/sites"),
      api.get("/monitoring/alerts"),
    ]).then(([p, s, a]) => { setPoints(p); setSites(s); setAlerts(a); setLoaded(true); }).catch(() => setLoaded(true));
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  if (!loaded) return <Loading label="Loading monitoring" />;

  const chartData = points.map((p) => ({ ...p, t: new Date(p.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }));

  return (
    <div data-testid="monitoring-page">
      <PageHeader title="Monitoring" breadcrumb="cannon / monitoring" subtitle={`~60s health checks · ${points.length} / 1440 points retained`} />

      {alerts.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="monitoring-alerts">
          {alerts.map((a, i) => (
            <div key={i} className={cn("flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-mono",
              a.level === "error" ? "border-rose-500/20 bg-rose-500/5 text-rose-300" : "border-amber-500/20 bg-amber-500/5 text-amber-300")}>
              <AlertTriangle className="h-4 w-4" /> <a href={`/console?service=${a.service||"server"}&command=logs`}>{a.message} → Investigate</a>
            </div>
          ))}
        </div>
      )}

      <LiveNetwork />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {[{ k: "cpu", c: "#0070f3", label: "CPU %" }, { k: "mem", c: "#10b981", label: "RAM %" }].map((m) => (
          <div key={m.k} className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4">
              <Activity className="h-3.5 w-3.5" /> {m.label}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#18181b" vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 10, fill: "#52525b" }} minTickGap={40} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#52525b" }} width={28} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0c0c0e", border: "1px solid #27272a", fontSize: 12, fontFamily: "IBM Plex Mono" }} />
                <Line type="monotone" dataKey={m.k} stroke={m.c} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>

      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <div className="border-b border-zinc-800 px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <HardDrive className="h-3.5 w-3.5" /> Per-site status
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left text-xs font-semibold uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-2">Site</th><th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Status</th><th className="px-4 py-2">Storage</th><th className="px-4 py-2">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => (
              <tr key={s.slug} className="border-b border-zinc-800/70 hover:bg-zinc-900/40" data-testid={`monitor-row-${s.slug}`}>
                <td className="px-4 py-3"><div className="text-zinc-200">{s.name}</div><div className="font-mono text-xs text-zinc-500">{s.slug}</div></td>
                <td className="px-4 py-3"><StatusBadge status={s.type} /></td>
                <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{fmtBytes(s.storage_bytes)}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.enabled ? <span className="text-emerald-400">yes</span> : <span className="text-zinc-500">no</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ServerServices />
    </div>
  );
}
